import {
  ComboboxAction,
  getComboboxActionFromKey,
  getUpdatedIndex,
} from "@utilities/keyboardUtils"
import {
  getDataAttributes,
  getIndexByLetter,
  getSelectableOptionsFromSegments,
  normalizeOptions,
  type Segment,
} from "@utilities/selectUtils"
import { useId } from "@utilities/useId"
import React, {
  forwardRef,
  type SelectHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import type { SelectItem, SelectOption } from "./Select"
import { SelectOptions } from "./SelectOptions"

interface ToggleEvent extends Event {
  newState: "closed" | "open"
  oldState: "closed" | "open"
}

export interface SelectPopoverProps {
  "aria-describedby"?: string
  "aria-label"?: string
  "aria-labelledby"?: string
  defaultValue?: string
  disabled?: boolean
  id?: string
  invalid?: boolean
  /** A flat or mixed array of options and option groups. */
  options: SelectItem[]
  /** Placeholder text shown in the trigger button when no option is selected. `null` suppresses the placeholder. */
  placeholder: string | null
  required?: boolean
  triggerRef?: React.RefObject<HTMLButtonElement>
  value?: string
}

export type SelectPopoverElementProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  keyof SelectPopoverProps | "multiple"
> &
  SelectPopoverProps

const Group = ({
  children,
  disabled,
  id,
  label,
}: {
  children: React.ReactNode
  disabled?: boolean
  id: string
  label: string
}) => (
  <ul aria-disabled={disabled || undefined} aria-labelledby={id} role="group">
    <li id={id} role="presentation">
      <span>{label}</span>
    </li>
    {children}
  </ul>
)

function renderListSegments(
  segments: Segment[],
  baseId: string,
  renderItem: (option: SelectOption, index: number) => React.ReactNode
) {
  let flatIndex = 0

  return segments.map((segment, segmentIndex) => {
    const startIndex = flatIndex
    flatIndex += segment.options.length

    if (segment.type === "group") {
      return (
        <Group
          key={`${segment.label}-${segmentIndex}`}
          disabled={segment.disabled}
          id={`${baseId}-group-${segmentIndex}`}
          label={segment.label}
        >
          {segment.options.map((opt, i) => renderItem(opt, startIndex + i))}
        </Group>
      )
    }

    return (
      <ul key={`options-${segmentIndex}`} role="presentation">
        {segment.options.map((opt, i) => renderItem(opt, startIndex + i))}
      </ul>
    )
  })
}

export const SelectPopover = forwardRef<
  HTMLSelectElement,
  SelectPopoverElementProps
>(
  (
    {
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      defaultValue,
      disabled,
      id,
      invalid,
      onBlur,
      onChange,
      onClick,
      onFocus,
      options,
      placeholder,
      required,
      triggerRef,
      value,
      ...props
    }: SelectPopoverElementProps,
    ref
  ) => {
    const stableId = useId()
    const baseId = id || `tds-select-popover-${stableId}`

    const [isOpen, setIsOpen] = useState(false)
    const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
      defaultValue ?? null
    )
    const [activeIndex, setActiveIndex] = useState(-1)

    const isControlled = value !== undefined
    const selectedValue = isControlled ? (value ?? null) : uncontrolledValue

    const { allOptions, segments } = useMemo(() => {
      const segments = normalizeOptions(options)
      const allOptions = getSelectableOptionsFromSegments(segments)
      return { allOptions, segments }
    }, [options])

    const selectedOption = allOptions.find((opt) => opt.value === selectedValue)

    const activeOption = activeIndex >= 0 ? allOptions[activeIndex] : undefined
    const activeOptionId = activeOption
      ? `${baseId}-option-${activeOption.value}`
      : undefined

    const popoverRef = useRef<HTMLDivElement>(null)
    const internalTriggerRef = useRef<HTMLButtonElement | null>(null)
    const resolvedTriggerRef = triggerRef ?? internalTriggerRef
    const hiddenSelectRef = useRef<HTMLSelectElement | null>(null)
    const pendingChangeValueRef = useRef<string | null>(null)
    const searchStringRef = useRef("")
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const setHiddenSelectRef = useCallback(
      (element: HTMLSelectElement | null) => {
        hiddenSelectRef.current = element
        if (typeof ref === "function") {
          ref(element)
        } else if (ref) {
          ref.current = element
        }
      },
      [ref]
    )

    useEffect(() => {
      const popoverEl = popoverRef.current
      if (!popoverEl) return

      const handleToggle = (e: Event) => {
        const newIsOpen = (e as ToggleEvent).newState === "open"
        setIsOpen(newIsOpen)

        if (newIsOpen) {
          const index = allOptions.findIndex(
            (opt) => opt.value === selectedValue && !opt.disabled
          )
          if (index >= 0) {
            setActiveIndex(index)
          } else {
            // Find first non-disabled option
            const firstEnabledIndexOrFirstIndex = Math.max(
              allOptions.findIndex((opt) => !opt.disabled),
              0
            )
            setActiveIndex(firstEnabledIndexOrFirstIndex)
          }
        } else {
          setActiveIndex(-1)
        }
      }

      popoverEl.addEventListener("toggle", handleToggle)
      return () => popoverEl.removeEventListener("toggle", handleToggle)
    }, [allOptions, selectedValue])

    useEffect(() => {
      if (activeOptionId) {
        document
          .getElementById(activeOptionId)
          ?.scrollIntoView({ block: "nearest" })
      }
    }, [activeOptionId])

    useEffect(
      () => () => {
        clearTimeout(searchTimeoutRef.current)
      },
      []
    )

    const openPopover = () =>
      popoverRef.current?.showPopover({ source: resolvedTriggerRef.current! })

    const closePopover = () => popoverRef.current?.hidePopover()

    const commitSelection = useCallback(
      (nextValue: string) => {
        if (nextValue === selectedValue) {
          closePopover()
          return
        }

        if (!isControlled) {
          setUncontrolledValue(nextValue)
        }

        const hiddenEl = hiddenSelectRef.current
        if (hiddenEl) {
          pendingChangeValueRef.current = nextValue
          hiddenEl.value = nextValue
          hiddenEl.dispatchEvent(new Event("change", { bubbles: true }))
          if (isControlled) {
            hiddenEl.value = value ?? ""
          }
        }

        closePopover()
      },
      [isControlled, selectedValue, value]
    )

    const selectActiveOption = useCallback(() => {
      if (activeOption && !activeOption.disabled) {
        commitSelection(activeOption.value!)
        return true
      }
      return false
    }, [activeOption, commitSelection])

    const commitActiveOptionOrClose = useCallback(() => {
      if (!selectActiveOption()) {
        closePopover()
      }
    }, [selectActiveOption])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return

      // Tab must not be prevented — allow native focus movement
      if (e.key === "Tab" && isOpen) {
        commitActiveOptionOrClose()
        return
      }

      const action = getComboboxActionFromKey(e, isOpen)
      if (!action) return

      e.preventDefault()

      switch (action) {
        case ComboboxAction.OpenPopup:
          openPopover()
          break
        case ComboboxAction.CommitAndClose:
          commitActiveOptionOrClose()
          break
        case ComboboxAction.MoveFirst:
        case ComboboxAction.MoveLast:
          if (!isOpen) openPopover()
          setActiveIndex(getUpdatedIndex(activeIndex, action, allOptions))
          break
        case ComboboxAction.MoveNext:
        case ComboboxAction.MovePrevious:
          setActiveIndex(getUpdatedIndex(activeIndex, action, allOptions))
          break
        case ComboboxAction.TypeCharacter: {
          if (!isOpen) openPopover()

          clearTimeout(searchTimeoutRef.current)
          searchStringRef.current += e.key
          searchTimeoutRef.current = setTimeout(() => {
            searchStringRef.current = ""
          }, 500)

          const searchIndex = getIndexByLetter(
            allOptions,
            searchStringRef.current,
            activeIndex
          )
          if (searchIndex >= 0) {
            setActiveIndex(searchIndex)
          }
          break
        }
        case ComboboxAction.ClosePopup:
          closePopover()
          break
      }
    }

    const createStableEventTarget = (
      target: HTMLSelectElement,
      valueSnapshot: string
    ) => {
      const proxy = Object.create(target)
      Object.defineProperties(proxy, {
        disabled: {
          configurable: true,
          enumerable: true,
          value: target.disabled,
        },
        form: { configurable: true, enumerable: true, value: target.form },
        name: { configurable: true, enumerable: true, value: target.name },
        type: { configurable: true, enumerable: true, value: target.type },
        value: { configurable: true, enumerable: true, value: valueSnapshot },
      })
      return proxy as HTMLSelectElement
    }

    const withSelectTarget = useCallback(
      <
        SourceEvent extends React.SyntheticEvent,
        TargetEvent extends React.SyntheticEvent<HTMLSelectElement>,
      >(
        event: SourceEvent,
        target: HTMLSelectElement,
        currentTarget: HTMLSelectElement = target
      ) => {
        return Object.defineProperties(Object.create(event), {
          currentTarget: { value: currentTarget },
          target: { value: target },
        }) as TargetEvent
      },
      []
    )

    const handleHiddenSelectChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onChange) return

        const valueSnapshot =
          pendingChangeValueRef.current ?? event.currentTarget.value
        pendingChangeValueRef.current = null

        const stableEvent = withSelectTarget<
          React.ChangeEvent<HTMLSelectElement>,
          React.ChangeEvent<HTMLSelectElement>
        >(
          event,
          createStableEventTarget(
            event.target as HTMLSelectElement,
            valueSnapshot
          ),
          createStableEventTarget(event.currentTarget, valueSnapshot)
        )

        onChange(stableEvent)
      },
      [onChange, withSelectTarget]
    )

    const forwardEventToSelect = useCallback(
      <
        ButtonEvent extends React.SyntheticEvent<HTMLButtonElement>,
        SelectEvent extends React.SyntheticEvent<HTMLSelectElement>,
      >(
        event: ButtonEvent,
        handler?: (event: SelectEvent) => void
      ) => {
        if (!handler) return

        const hiddenEl = hiddenSelectRef.current
        if (!hiddenEl) return

        const stableEvent = withSelectTarget<ButtonEvent, SelectEvent>(
          event,
          hiddenEl
        )

        handler(stableEvent)
      },
      [withSelectTarget]
    )

    const handleButtonBlur = useCallback(
      (event: React.FocusEvent<HTMLButtonElement>) => {
        forwardEventToSelect<
          React.FocusEvent<HTMLButtonElement>,
          React.FocusEvent<HTMLSelectElement>
        >(event, onBlur)
      },
      [forwardEventToSelect, onBlur]
    )

    const handleButtonClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        forwardEventToSelect<
          React.MouseEvent<HTMLButtonElement>,
          React.MouseEvent<HTMLSelectElement>
        >(event, onClick)
      },
      [forwardEventToSelect, onClick]
    )

    const handleButtonFocus = useCallback(
      (event: React.FocusEvent<HTMLButtonElement>) => {
        forwardEventToSelect<
          React.FocusEvent<HTMLButtonElement>,
          React.FocusEvent<HTMLSelectElement>
        >(event, onFocus)
      },
      [forwardEventToSelect, onFocus]
    )

    const renderListOption = (option: SelectOption, index: number) => {
      const { disabled, divider, label, value } = option
      if (divider) return <li key={index} role="separator" />
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard events are handled on the combobox button
        <li
          key={value}
          {...getDataAttributes(option)}
          aria-disabled={disabled || undefined}
          aria-selected={value === selectedValue}
          className={
            value === activeOption?.value
              ? "tds-select-option--active"
              : undefined
          }
          id={`${baseId}-option-${value}`}
          onClick={disabled ? undefined : () => commitSelection(value!)}
          role="option"
        >
          {label}
        </li>
      )
    }

    return (
      <>
        <button
          ref={resolvedTriggerRef}
          popovertarget={baseId}
          aria-activedescendant={activeOptionId}
          aria-controls={baseId}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled ? "true" : undefined}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-invalid={invalid ? "true" : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-required={required ? "true" : undefined}
          disabled={disabled}
          onBlur={handleButtonBlur}
          onClick={handleButtonClick}
          onFocus={handleButtonFocus}
          onKeyDown={handleKeyDown}
          role="combobox"
          type="button"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </button>
        <div
          ref={popoverRef}
          popover=""
          id={baseId}
          role="listbox"
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid ? "true" : undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-required={required ? "true" : undefined}
        >
          {renderListSegments(segments, baseId, renderListOption)}
        </div>
        <select
          {...props}
          ref={setHiddenSelectRef}
          aria-hidden
          className="tds-select-hidden-select"
          disabled={disabled}
          id={`${baseId}-control`}
          onChange={handleHiddenSelectChange}
          required={required}
          tabIndex={-1}
          value={selectedValue ?? ""}
        >
          <option value="" />
          <SelectOptions items={options} />
        </select>
      </>
    )
  }
)

SelectPopover.displayName = "SelectPopover"
